import React, { useEffect, useState } from 'react';
import { DEBUG, getBaseURL } from '../../config/runtime';
import { fireToast } from '../../event/toastEvent';
import {
  TYPE as WINDOW_CLOSE_EVENT_TYPE,
  WindowCloseEventData,
  WindowCloseEventDataType,
} from '../../event/window_close_event';
import { match } from '../../util/arr';
import { NotificationItem } from './notification_item';
import type { NotificationItemData } from './notification_item_data';
import {
  NotificationListRefreshEventData,
  TYPE,
} from './notification_list_refresh_event';

interface Props {}

const WINDOW_ID = 'notifications';

const ERROR_MESSAGE_GENERIC = 'Unable to retrieve notification list.';

const LOCAL_STORAGE_NOTIFICATIONS_KEY = 'notifications';

const POLL_INTERVAL_IN_SECS = 10000;

export function NotificationList(props: Props) {
  const [data, setData] = useState<NotificationItemData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  function getLocalNotifications() {
    return JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_NOTIFICATIONS_KEY) ?? '[]'
    ) as NotificationItemData[];
  }

  function pollNotifications() {
    return setInterval(async () => {
      const localNotifications = getLocalNotifications();
      const notComplete = localNotifications.find(
        (n) => n.status !== 'SUCCESS' && n.status !== 'FAIL'
      );
      if (notComplete) {
        fetchData();
      }
    }, POLL_INTERVAL_IN_SECS);
  }

  const fetchData = async () => {
    const url = DEBUG
      ? `${getBaseURL()}/v1/app/queue?session=oeu33sonaijcnqg5lf8c60u7q1`
      : `${getBaseURL()}/v1/app/queue`;
    const response = await fetch(url);
    if (!response.ok) {
      fireToast(ERROR_MESSAGE_GENERIC);
      return;
    }

    const respData = await response.json();
    if (respData?.object !== 'list') {
      fireToast(ERROR_MESSAGE_GENERIC);
      return;
    }

    const localNotifications = getLocalNotifications();
    const isMatch = match(respData.data, localNotifications);
    setShowAlert(!isMatch);
    localStorage.setItem('notifications', JSON.stringify(respData.data));
    setData(respData.data);
  };

  // Refetch data when switching between tabs.
  useEffect(() => {
    const onTabVisibilityChange = () => {};
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    });

    return () =>
      document.removeEventListener('visibilitychange', onTabVisibilityChange);
  }, []);

  // Init polling of notifications.
  useEffect(() => {
    const intervalID = pollNotifications();
    return () => {
      clearInterval(intervalID);
    };
  }, []);

  // Add Notification List Refresh Event listener
  useEffect(() => {
    const onNotificationListRefresh = (
      e: CustomEvent<NotificationListRefreshEventData>
    ) => {
      fetchData();
    };

    document.addEventListener(TYPE, onNotificationListRefresh);

    return () => {
      document.removeEventListener(TYPE, onNotificationListRefresh);
    };
  }, []);

  // Add Window Close Event listener
  useEffect(() => {
    const onWindowCloseEvent = (e: CustomEvent<WindowCloseEventData>) => {
      if (
        e.detail?.type === WindowCloseEventDataType.NAV_TOP &&
        typeof e.detail?.window_id === 'string' &&
        e.detail?.window_id !== WINDOW_ID
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(WINDOW_CLOSE_EVENT_TYPE, onWindowCloseEvent);

    return () => {
      document.removeEventListener(WINDOW_CLOSE_EVENT_TYPE, onWindowCloseEvent);
    };
  }, []);

  // Fetch notification data.
  useEffect(() => {
    fetchData();
  }, []);

  // Update local storage with latest notification data.
  useEffect(() => {
    if (isOpen) {
      setShowAlert(false);
    }
  }, [isOpen]);

  const removeTask = (id: string) => {
    const items = data.filter((d) => d.id !== id);
    localStorage.setItem('notifications', JSON.stringify(items));
    setData(items);
    const params = new URLSearchParams('visible=false');
    const response = fetch(`${getBaseURL()}/v1/app/queue/${id}/visibility`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    // TODO: Call endpoint to deactivate task.
  };

  return (
    <>
      {/* Alert (bell) icon */}
      <div className={`inline-flex h-6 w-6 justify-center`}>
        <span
          className={`pointer-events-none absolute right-1 top-1 h-3 w-3 ${
            showAlert ? 'flex' : 'hidden'
          }`}
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75"></span>
          <span className="relative inline-flex h-3 w-3 rounded-full bg-sky-500"></span>
        </span>
        <svg
          className="h-8 w-8 fill-slate-100 stroke-slate-100"
          onClick={() => {
            setIsOpen(!isOpen);

            // Dispatch close window event if notification list is open.
            if (!isOpen) {
              const ev = new CustomEvent<WindowCloseEventData>(
                WINDOW_CLOSE_EVENT_TYPE,
                {
                  detail: {
                    window_id: WINDOW_ID,
                    type: WindowCloseEventDataType.NAV_TOP,
                  },
                }
              );
              document.dispatchEvent(ev);
            }
          }}
          strokeWidth="1.5"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          ></path>
        </svg>

        <div
          className={`${
            isOpen ? 'flex' : 'hidden'
          } fixed top-8 right-0 w-[500px] flex-col bg-white shadow-left`}
          style={{ height: 'calc(100% - 2rem)' }}
        >
          {/* <div className="absolute -top-2 left-1/2 -z-10 -mt-1 h-6 w-6 -translate-x-1/2 rotate-45 transform border bg-slate-200"></div> */}
          <header className="flex h-6 items-center justify-center bg-slate-200 py-4">
            <div className="flex w-full items-center justify-between px-6">
              <label className="block self-start text-lg font-semibold text-slate-700">
                Notifications
              </label>
              <svg
                className="h-6 w-6 cursor-pointer self-end text-slate-500"
                onClick={() => setIsOpen(false)}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </header>
          <div className="flex-1 overflow-y-scroll">
            {data.length === 0 ? (
              <div className="top-28 z-0 flex w-full flex-col">
                <svg
                  className="mx-auto mt-12 mb-4 block h-24 w-24 stroke-slate-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
                <label className="text-center font-semibold">
                  No new notifications from this session
                </label>
              </div>
            ) : null}
            {data.map((d) => (
              <NotificationItem key={d.id} data={d} removeTask={removeTask} />
            ))}
          </div>
          <footer className="flex h-7 w-full items-center justify-end bg-slate-200">
            <span className="px-4">
              <label className="text-sm text-slate-700">count: </label>
              <label className="text-sm text-slate-800">{data.length}</label>
            </span>
          </footer>
        </div>
      </div>
    </>
  );
}
